import {
  deleteV1CompaniesCompanyIdFuelSurchargeSchedulesId,
  FuelSurchargeScheduleType,
  getV1CompaniesCompanyIdFuelSurchargeSchedules,
  GetV1CompaniesCompanyIdFuelSurchargeSchedulesData,
  getV1CompaniesCompanyIdFuelSurchargeSchedulesTypeahead,
  patchV1CompaniesCompanyIdFuelSurchargeSchedulesId,
  postV1CompaniesCompanyIdFuelSurchargeSchedules,
} from '@treadinc/horizon-api-spec';
import { useState } from 'react';

import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FuelSurchargeSchedule, FuelSurchargeScheduleTypeahead } from './model';

interface GetCompanyFuelSurchargeSchedulesParams {
  limit?: number;
  cursor?: string;
}

interface CreateFuelSurchargeScheduleParams {
  name: string;
  fuelPriceIndexId: string;
  surchargeType: FuelSurchargeScheduleType;
}

interface UpdateFuelSurchargeScheduleParams {
  id: string;
  name: string;
  fuelPriceIndexId: string;
  surchargeType: FuelSurchargeScheduleType;
}

interface GetFuelSurchargeSchedulesTypeaheadParams {
  searchQuery?: string;
  cursor?: string;
}

export function useFuelSurchargeSchedule() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [isLoading, setIsLoading] = useState(false);

  const getCompanyFuelSurchargeSchedules = async ({
    limit = 20,
    cursor,
  }: GetCompanyFuelSurchargeSchedulesParams) => {
    const query = {
      'page[limit]': limit,
      ...(cursor && { 'page[after]': cursor }),
    } as GetV1CompaniesCompanyIdFuelSurchargeSchedulesData['query'];

    try {
      setIsLoading(true);
      const response = await getV1CompaniesCompanyIdFuelSurchargeSchedules({
        path: { 'company-id': companyId },
        query,
      });
      const parsedData = response.data.data.map(FuelSurchargeSchedule.parse);
      const pagination = extractPagination(response);
      return { data: parsedData, pagination };
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createFuelSurchargeSchedule = async ({
    name,
    fuelPriceIndexId,
    surchargeType,
  }: CreateFuelSurchargeScheduleParams) => {
    try {
      const response = await postV1CompaniesCompanyIdFuelSurchargeSchedules({
        path: { 'company-id': companyId },
        body: {
          name,
          fuel_price_index_id: fuelPriceIndexId,
          surcharge_type: surchargeType,
        },
      });
      return FuelSurchargeSchedule.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const updateFuelSurchargeSchedule = async ({
    id,
    name,
    fuelPriceIndexId,
    surchargeType,
  }: UpdateFuelSurchargeScheduleParams) => {
    try {
      const response = await patchV1CompaniesCompanyIdFuelSurchargeSchedulesId({
        path: { 'company-id': companyId, id },
        body: {
          name,
          fuel_price_index_id: fuelPriceIndexId,
          surcharge_type: surchargeType,
        },
      });

      return FuelSurchargeSchedule.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const deleteFuelSurchargeSchedule = async (id: string) => {
    try {
      await deleteV1CompaniesCompanyIdFuelSurchargeSchedulesId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const getFuelSurchargeSchedulesTypeahead = async ({
    searchQuery,
    cursor,
  }: GetFuelSurchargeSchedulesTypeaheadParams) => {
    try {
      const query = {
        'page[limit]': 20,
        ...(searchQuery && { 'search[query]': searchQuery.trim() }),
        ...(cursor && { 'page[after]': cursor }),
      };

      const response = await getV1CompaniesCompanyIdFuelSurchargeSchedulesTypeahead({
        path: { 'company-id': companyId },
        query,
      });

      const fuelSurchargeSchedules = response.data.data.map((schedule) =>
        FuelSurchargeScheduleTypeahead.parse(schedule),
      );

      return { data: fuelSurchargeSchedules, pagination: extractPagination(response) };
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  return {
    getCompanyFuelSurchargeSchedules,
    createFuelSurchargeSchedule,
    updateFuelSurchargeSchedule,
    deleteFuelSurchargeSchedule,
    getFuelSurchargeSchedulesTypeahead,
    isLoading,
  };
}
